/**
 * Desktop app config: session tabs + recent files in `{appConfigDir}/config.ini`.
 * Unknown keys/sections are ignored (zero migrations).
 */

import { parse as parseIni, stringify as stringifyIni } from "ini";
import { isNativeShell } from "./platform/detect";

export const DESKTOP_CONFIG_FILENAME = "config.ini";
export const DEFAULT_MAX_RECENTS = 20;

export interface DesktopRecentEntry {
  path: string;
  openedAt: string;
}

export interface DesktopSessionConfig {
  paths: string[];
  active: number;
}

export interface DesktopConfig {
  session: DesktopSessionConfig;
  recents: DesktopRecentEntry[];
  maxRecents: number;
}

export function emptyDesktopConfig(): DesktopConfig {
  return {
    session: { paths: [], active: 0 },
    recents: [],
    maxRecents: DEFAULT_MAX_RECENTS,
  };
}

/** Decode INI text into typed config; tolerate missing/partial data. */
export function desktopConfigFromIni(text: string): DesktopConfig {
  const raw = parseIni(text) as Record<string, Record<string, unknown> | undefined>;
  const sessionSection = raw.session ?? {};
  const recentsSection = raw.recents ?? {};

  const paths: string[] = [];
  for (let index = 0; ; index += 1) {
    const value = sessionSection[`open_${index}`];
    if (typeof value !== "string" || value.trim().length === 0) {
      break;
    }
    paths.push(value.trim());
  }

  const activeRaw = sessionSection.active;
  const activeParsed = typeof activeRaw === "string" || typeof activeRaw === "number"
    ? Number(activeRaw)
    : 0;
  const active = Number.isFinite(activeParsed) ? Math.max(0, Math.floor(activeParsed)) : 0;

  const maxRaw = recentsSection.max;
  const maxParsed = typeof maxRaw === "string" || typeof maxRaw === "number"
    ? Number(maxRaw)
    : DEFAULT_MAX_RECENTS;
  const maxRecents = Number.isFinite(maxParsed) && maxParsed > 0
    ? Math.floor(maxParsed)
    : DEFAULT_MAX_RECENTS;

  const recents: DesktopRecentEntry[] = [];
  for (let index = 0; ; index += 1) {
    const pathValue = recentsSection[`${index}_path`];
    if (typeof pathValue !== "string" || pathValue.trim().length === 0) {
      break;
    }
    const openedRaw = recentsSection[`${index}_opened_at`];
    const openedAt = typeof openedRaw === "string" && openedRaw.trim().length > 0
      ? openedRaw.trim()
      : new Date(0).toISOString();
    recents.push({ path: pathValue.trim(), openedAt });
  }

  return {
    session: {
      paths,
      active: paths.length === 0 ? 0 : Math.min(active, paths.length - 1),
    },
    recents,
    maxRecents,
  };
}

/** Encode typed config to INI text. */
export function desktopConfigToIni(config: DesktopConfig): string {
  const session: Record<string, string | number> = {
    active: config.session.paths.length === 0
      ? 0
      : Math.min(Math.max(0, config.session.active), config.session.paths.length - 1),
  };
  config.session.paths.forEach((path, index) => {
    session[`open_${index}`] = path;
  });

  const recents: Record<string, string | number> = {
    max: config.maxRecents > 0 ? config.maxRecents : DEFAULT_MAX_RECENTS,
  };
  config.recents.forEach((entry, index) => {
    recents[`${index}_path`] = entry.path;
    recents[`${index}_opened_at`] = entry.openedAt;
  });

  return stringifyIni(
    { session, recents },
    { whitespace: true },
  );
}

export function touchRecentInConfig(config: DesktopConfig, path: string, openedAt = new Date().toISOString()): DesktopConfig {
  const normalized = path.trim();
  if (!normalized) {
    return config;
  }
  const max = config.maxRecents > 0 ? config.maxRecents : DEFAULT_MAX_RECENTS;
  const rest = config.recents.filter(entry => entry.path !== normalized);
  return {
    ...config,
    recents: [{ path: normalized, openedAt }, ...rest].slice(0, max),
  };
}

export function setSessionInConfig(config: DesktopConfig, paths: string[], active: number): DesktopConfig {
  const cleaned = paths.map(path => path.trim()).filter(Boolean);
  return {
    ...config,
    session: {
      paths: cleaned,
      active: cleaned.length === 0 ? 0 : Math.min(Math.max(0, active), cleaned.length - 1),
    },
  };
}

export function clearRecentsInConfig(config: DesktopConfig): DesktopConfig {
  return { ...config, recents: [] };
}

export function removeRecentPathsInConfig(config: DesktopConfig, missing: ReadonlySet<string>): DesktopConfig {
  if (missing.size === 0) {
    return config;
  }
  const preferred = config.session.paths[config.session.active];
  const paths = config.session.paths.filter(path => !missing.has(path));
  return withClampedSessionActive({
    ...config,
    recents: config.recents.filter(entry => !missing.has(entry.path)),
    session: {
      paths,
      active: config.session.active,
    },
  }, preferred && !missing.has(preferred) ? preferred : undefined);
}

/** Fix active index after filtering session paths. */
export function withClampedSessionActive(config: DesktopConfig, preferredPath?: string): DesktopConfig {
  const { paths } = config.session;
  if (paths.length === 0) {
    return { ...config, session: { paths: [], active: 0 } };
  }
  if (preferredPath) {
    const index = paths.indexOf(preferredPath);
    if (index >= 0) {
      return { ...config, session: { paths, active: index } };
    }
  }
  return {
    ...config,
    session: {
      paths,
      active: Math.min(config.session.active, paths.length - 1),
    },
  };
}

let cachedConfig: DesktopConfig | undefined;
let writeTimer: ReturnType<typeof setTimeout> | undefined;
let writeInFlight: Promise<void> | undefined;

async function readConfigFromDisk(): Promise<DesktopConfig> {
  const { loadDesktopConfigText } = await import("./platform");
  const text = await loadDesktopConfigText();
  if (text === undefined) {
    return emptyDesktopConfig();
  }
  try {
    return desktopConfigFromIni(text);
  } catch (error) {
    console.warn("Failed to parse desktop config.ini", error);
    return emptyDesktopConfig();
  }
}

async function writeConfigToDisk(config: DesktopConfig): Promise<void> {
  const { saveDesktopConfigText } = await import("./platform");
  await saveDesktopConfigText(desktopConfigToIni(config));
}

/** Load config (cached after first read). No-op shape on web. */
export async function loadDesktopConfig(): Promise<DesktopConfig> {
  if (!isNativeShell()) {
    return emptyDesktopConfig();
  }
  if (cachedConfig) {
    return cachedConfig;
  }
  cachedConfig = await readConfigFromDisk();
  return cachedConfig;
}

/** Replace in-memory config and schedule a debounced disk write. */
export async function updateDesktopConfig(
  updater: (current: DesktopConfig) => DesktopConfig,
  options: { flush?: boolean } = {},
): Promise<DesktopConfig> {
  if (!isNativeShell()) {
    return emptyDesktopConfig();
  }
  const current = await loadDesktopConfig();
  const next = updater(current);
  cachedConfig = next;
  if (options.flush) {
    await flushDesktopConfig();
  } else {
    scheduleDesktopConfigWrite();
  }
  return next;
}

function scheduleDesktopConfigWrite(): void {
  if (writeTimer !== undefined) {
    clearTimeout(writeTimer);
  }
  writeTimer = setTimeout(() => {
    writeTimer = undefined;
    void flushDesktopConfig();
  }, 200);
}

/** Flush pending config writes immediately. */
export async function flushDesktopConfig(): Promise<void> {
  if (!isNativeShell() || !cachedConfig) {
    return;
  }
  if (writeTimer !== undefined) {
    clearTimeout(writeTimer);
    writeTimer = undefined;
  }
  const snapshot = cachedConfig;
  writeInFlight = (writeInFlight ?? Promise.resolve())
    .catch(() => undefined)
    .then(async () => {
      try {
        await writeConfigToDisk(snapshot);
      } catch (error) {
        console.warn("[curva] failed to write desktop config.ini", error);
        throw error;
      }
    });
  await writeInFlight;
}

export async function touchRecent(path: string): Promise<DesktopConfig> {
  return updateDesktopConfig(config => touchRecentInConfig(config, path));
}

export async function setDesktopSession(paths: string[], active: number): Promise<DesktopConfig> {
  return updateDesktopConfig(config => {
    let next = setSessionInConfig(config, paths, active);
    const ordered = next.session.paths;
    const activePath = ordered[next.session.active];
    for (const path of ordered) {
      if (path !== activePath) {
        next = touchRecentInConfig(next, path);
      }
    }
    if (activePath) {
      next = touchRecentInConfig(next, activePath);
    }
    return next;
  });
}

export async function clearDesktopRecents(): Promise<DesktopConfig> {
  return updateDesktopConfig(clearRecentsInConfig, { flush: true });
}

/**
 * Return recents after pruning paths that no longer exist (native only).
 * Persists the pruned list when anything was removed.
 */
export async function listDesktopRecents(): Promise<DesktopRecentEntry[]> {
  if (!isNativeShell()) {
    return [];
  }
  const config = await loadDesktopConfig();
  const { curvaFileExists } = await import("./platform");
  const missing = new Set<string>();
  for (const entry of config.recents) {
    try {
      if (!(await curvaFileExists(entry.path))) {
        missing.add(entry.path);
      }
    } catch {
      // Keep entries we cannot probe (permission / transient errors).
    }
  }
  if (missing.size === 0) {
    return config.recents;
  }
  const next = await updateDesktopConfig(current => removeRecentPathsInConfig(current, missing), { flush: true });
  return next.recents;
}

/** Test helper: clear the in-memory cache between tests. */
export function resetDesktopConfigCacheForTests(): void {
  cachedConfig = undefined;
  if (writeTimer !== undefined) {
    clearTimeout(writeTimer);
    writeTimer = undefined;
  }
  writeInFlight = undefined;
}
